import PropTypes from "prop-types";
import React, { Component } from "react";
import { autobind } from "core-decorators";
import CuepointDotsContainer from "containers/cuepoint/CuepointDotsContainer";
import Hammer from "components/HammerContainer";
import classnames from "classnames";
import keycode from "keycode";
import styles from "./MobileChapterControlsSlider.css";

export default class MobileChapterControlsSlider extends Component {
  static propTypes = {
    chapterId: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    hideProgressBarProgress: PropTypes.bool.isRequired,
    onScrubEnd: PropTypes.func.isRequired,
    onScrubStart: PropTypes.func.isRequired,
    onSeek: PropTypes.func.isRequired,
    scrubbing: PropTypes.bool.isRequired,
    setSliderRef: PropTypes.func.isRequired,
    sliderLeft: PropTypes.number.isRequired,
    sliderWidth: PropTypes.number.isRequired,
    time: PropTypes.number.isRequired,
    seekBack: PropTypes.func,
    seekForward: PropTypes.func,
  };

  componentDidMount() {
    window.addEventListener("mouseup", this.handleMouseOut);
    window.addEventListener("touchend", this.handleMouseOut);
    window.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("touchmove", this.handleMouseMove);
  }

  componentWillUnmount() {
    window.removeEventListener("touchend", this.handleMouseOut);
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseup", this.handleMouseOut);
    window.removeEventListener("touchmove", this.handleMouseMove);
  }

  get percentPlayed() {
    return Math.min(1, Math.max(0, this.props.time / this.props.duration));
  }

  @autobind
  handleMouseDown(e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.onScrubStart();
  }

  @autobind
  handleMouseMove(e) {
    if (this.props.scrubbing) {
      let clientX;
      if (e.touches) {
        clientX = e.touches[0].clientX;
      } else {
        clientX = e.clientX;
      }
      e.preventDefault();
      e.stopPropagation();
      const sliderLeft = this.sliderEl.getBoundingClientRect().left;
      const clickOffset = clientX - sliderLeft;
      const percentOffset = Math.min(
        0.99,
        Math.max(0, clickOffset / this.props.sliderWidth),
      ); // don't seek off the end
      const newTime = percentOffset * this.props.duration;
      this.props.onSeek(newTime);
    }
  }

  @autobind
  handleMouseOut(e) {
    if (this.props.scrubbing) {
      e.preventDefault();
      e.stopPropagation();
      this.props.onScrubEnd();
    }
  }

  @autobind
  handleSliderContainerUpdate(el) {
    this.sliderEl = el;
    this.props.setSliderRef(el);
  }

  handleKeyUp = (event) => {
    switch (keycode(event).toLowerCase()) {
      case "left":
        this.props?.seekBack();
        break;
      case "right":
        this.props?.seekForward();
        break;
      default:
        break;
    }
  };

  renderSliderHandle() {
    if (this.props.hideProgressBarProgress) {
      return null;
    }

    const className = classnames(styles.dragMarker, {
      [styles.dragMarkerActive]: this.props.scrubbing,
      [styles.dragMarkerIn]: this.props.time > 0,
    });

    return (
      <div
        className={className}
        onMouseDown={this.handleMouseDown}
        onTouchStart={this.handleMouseDown}
        aria-valuemax={this.props.duration}
        aria-valuemin={0}
        aria-valuenow={Math.round(this.props.time)}
        role="slider"
        onKeyUp={this.handleKeyUp}
        tabIndex={0}
        style={{
          left: `${Math.floor(this.percentPlayed * this.props.sliderWidth)}px`,
        }}
      >
        <div className={styles.dragMarkerHitArea} />
        <div className={styles.dragMarkerInner} />
      </div>
    );
  }

  @autobind
  handleHitAreaTap(e) {
    let offsetPercent =
      (e.center.x - this.props.sliderLeft) / this.props.sliderWidth;
    offsetPercent = Math.min(0.99, Math.max(0, offsetPercent));
    const newTime = offsetPercent * this.props.duration;
    this.props.onSeek(newTime);
  }

  renderProgress() {
    if (this.props.hideProgressBarProgress) {
      return null;
    }
    return (
      <div
        className={styles.sliderProgress}
        style={{
          width: `${this.percentPlayed * 100}%`,
        }}
      />
    );
  }

  renderHitArea() {
    return (
      <Hammer onTap={this.handleHitAreaTap}>
        <div className={styles.hitArea} />
      </Hammer>
    );
  }

  render() {
    return (
      <div className={styles.root}>
        {this.renderHitArea()}
        <div
          className={styles.sliderBackground}
          ref={this.handleSliderContainerUpdate}
        >
          {this.renderProgress()}
          {this.renderSliderHandle()}
          <CuepointDotsContainer chapterId={this.props.chapterId} />
        </div>
      </div>
    );
  }
}
